import { action, computed, observable, toJS } from 'mobx'
import {
  AddressInput,
  ContactDetailInput,
  Location,
  OfferInput,
  ProvisionInput,
  WizardInput
} from 'src/resources'
import { stores } from 'src/stores'
import { backend } from '../api'

export default class WizardStore {
  @observable private offerFinished: boolean

  // The wizard input object is split up to achieve observability of all fields
  @observable readProcessInfo: boolean

  @observable uuid: string

  basics = observable({
    funeralType: null,
    planningReason: null,
    beneficiary: null,
    previousPage: null,
    gclId: null,
    gaClientId: null
  })

  options = observable({
    syncWithZoho: true,
    sendNotifications: true,
    testingModeEnabled: false
  })

  location: Location = observable({
    province: null,
    city: null,
    available: false,
    zipCode: null,
    latitude: null,
    longitude: null,
    label: null,
    displayCategories: null
  })

  deceasedLocation: AddressInput = observable({
    locationType: null,
    city: null,
    zipCode: null,
    street: null,
    number: null,
    country: null
  })

  provision: ProvisionInput = observable({
    provisionReason: null,
    progress: null,
    timeFrame: null,
    birthYear: '',
    includeTrustAccountCosts: true
  })

  contactDetails: ContactDetailInput = observable({
    greeting: null,
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  })

  constructor(url: string) {
    this.offerFinished = false
    if (url.includes('dev.benu.at') || url.includes('localhost')) {
      this.options = observable({
        syncWithZoho: false,
        sendNotifications: false,
        testingModeEnabled: true
      })
    }
  }

  // TODO
  @action createOffer(callback: (result: OfferInput) => void) {
    const { funeralType, planningReason, beneficiary } = this.basics
    const wizardInput: WizardInput = {
      location: toJS(this.location),
      funeralType,
      planningReason,
      deceasedLocation: toJS(this.deceasedLocation),
      provision: toJS(this.provision),
      beneficiary,
      contactDetails: toJS(this.contactDetails),
      syncWithZoho: this.options.syncWithZoho,
      sendNotifications: this.options.sendNotifications,
      testingModeEnabled: this.options.testingModeEnabled,
      gclId: this.basics.gclId,
      gaClientId: this.basics.gaClientId,
      previousPage: this.basics.previousPage
    }
    return backend.createOffer(wizardInput, (result) => {
      if (result) {
        const offer: OfferInput = {
          ...wizardInput,
          ...result
        }
        stores.confStore.initWithOffer(offer)
        this.uuid = offer.uuid
        this.offerFinished = !!offer.contactDetails.phone
      }
      callback(result)
    })
  }

  @action saveContacts(callback: (result) => void) {
    return backend.saveContactDetails(
      this.uuid,
      this.contactDetails,
      (result) => {
        if (result) {
          this.offerFinished = true
          stores.confStore.setOfferContactDetails(
            this.contactDetails,
            null,
            true
          )
          callback(result)
        }
      }
    )
  }

  @computed get isOfferFinished() {
    return this.offerFinished
  }

  @computed get preamble() {
    switch (this.basics.planningReason) {
      case 'ACUTE_BEREAVEMENT':
        return '/eingetreten'
      case 'IMPENDING_BEREAVEMENT':
        return '/bevorstehend'
      case 'PROVISION':
        return '/vorsorge'
      default:
        return ''
    }
  }

  @computed get nextWizardStep() {
    if (!this.readProcessInfo) return '/planung/start/'
    if (!this.basics.planningReason) return '/planung/anliegen/'
    if (!this.basics.funeralType)
      return `/planung${this.preamble}/bestattungsart/`

    if (this.basics.planningReason === 'ACUTE_BEREAVEMENT') {
      if (!this.deceasedLocation || !this.deceasedLocation.locationType)
        return '/planung/eingetreten/ort/'
      if (!this.location || (!this.location.city && !this.location.latitude))
        return '/planung/eingetreten/plz/'
    } else if (this.basics.planningReason === 'IMPENDING_BEREAVEMENT') {
      if (!this.location || (!this.location.city && !this.location.latitude))
        return '/planung/bevorstehend/plz/'
    } else if (this.basics.planningReason === 'PROVISION') {
      if (!this.location || (!this.location.city && !this.location.latitude))
        return '/planung/vorsorge/plz/'
      if (!this.provision || !this.provision.provisionReason) {
        return '/planung/vorsorge/planungsgrund/'
      }
      if (!this.provision.birthYear) return '/planung/vorsorge/geburtsjahr/'
      if (!this.provision.progress) return '/planung/vorsorge/planungsstand/'
      if (!this.provision.timeFrame) return '/planung/vorsorge/zeitplan/'
    }

    if (!this.contactDetails || !this.contactDetails.lastName)
      return `/planung/angebot/angebotserstellung/`

    return ''
  }
}



// WEBPACK FOOTER //
// ./src/stores/WizardStore.ts